import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Wallet, ArrowRight, Loader2, RotateCcw,
    Send, ChevronDown, IndianRupee, Hash,
    CheckCircle,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency, getAmountDisplay } from '../../utilities/amountToTextHelper';

import {
    fetchAllWallets,
    fetchToLoadWallets,
    submitLoadWallet,
    clearSaveResult,
    resetLoadWallet,
    selectWallets,
    selectWalletsLoading,
    selectToWallets,
    selectToWalletsLoading,
    selectLwSaveStatus,
    selectLwSaveLoading,
    selectLwSaveError,
} from '../../slices/accountsSlice/loadWalletSlice';

import {
    fetchBankDetailsWithAvailableBalance,
    selectBankDetailsArray,
    selectBankDetailsLoading,
} from '../../slices/CommonSlice/bankDetailsSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const TRANSFER_FROM_OPTIONS = ['Bank', 'Wallet'];
const MODE_OPTIONS = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Digital Transaction', 'Cheque'];

// ── Shared styles (compact) ───────────────────────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const LoadWallet = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ─────────────────────────────────────────────────────────────────
    const wallets         = useSelector(selectWallets);
    const walletsLoading  = useSelector(selectWalletsLoading);
    const toWallets       = useSelector(selectToWallets);
    const toWalletsLoading = useSelector(selectToWalletsLoading);
    const bankList        = useSelector(selectBankDetailsArray);
    const bankLoading     = useSelector(selectBankDetailsLoading);
    const saveStatus      = useSelector(selectLwSaveStatus);
    const saveLoading     = useSelector(selectLwSaveLoading);
    const saveError       = useSelector(selectLwSaveError);

    // ── Form state ────────────────────────────────────────────────────────────
    const [transferFrom,  setTransferFrom]  = useState('');
    const [fromBank,      setFromBank]      = useState(null);
    const [fromWallet,    setFromWallet]    = useState(null);
    const [toWallet,      setToWallet]      = useState(null);
    const [modeOfPay,     setModeOfPay]     = useState('');
    const [transactionNo, setTransactionNo] = useState('');
    const [date,          setDate]          = useState(null);
    const [amount,        setAmount]        = useState('');
    const [remarks,       setRemarks]       = useState('');

    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchAllWallets());
        dispatch(fetchBankDetailsWithAvailableBalance());
        return () => { dispatch(resetLoadWallet()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // ── Transfer from change: clear downstream ────────────────────────────────
    useEffect(() => {
        setFromBank(null);
        setFromWallet(null);
        setToWallet(null);
        setModeOfPay('');
        setTransactionNo('');
    }, [transferFrom]);

    // ── From wallet change: fetch to-wallets ──────────────────────────────────
    useEffect(() => {
        if (transferFrom === 'Wallet' && fromWallet) {
            setToWallet(null);
            dispatch(fetchToLoadWallets(fromWallet.WalletId));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromWallet, transferFrom]);

    // ── Save result ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Wallet loaded successfully!');
            dispatch(clearSaveResult());
            handleReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus, saveError]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleReset = () => {
        setTransferFrom('');
        setFromBank(null);
        setFromWallet(null);
        setToWallet(null);
        setModeOfPay('');
        setTransactionNo('');
        setDate(null);
        setAmount('');
        setRemarks('');
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d*$/.test(val)) setAmount(val);
    };

    const handleSubmit = () => {
        if (!transferFrom)  { toast.warn('Please select transfer from type.');       return; }
        if (transferFrom === 'Bank'   && !fromBank)   { toast.warn('Please select source bank.');     return; }
        if (transferFrom === 'Wallet' && !fromWallet) { toast.warn('Please select source wallet.');   return; }
        if (!toWallet)  { toast.warn('Please select destination wallet.'); return; }
        if (!modeOfPay) { toast.warn('Please select mode of payment.'); return; }
        if (!transactionNo.trim()) { toast.warn('Please enter transaction / reference number.'); return; }
        if (!date)      { toast.warn('Please select transaction date.'); return; }
        if (!amount || parseFloat(amount) <= 0) { toast.warn('Please enter a valid amount.'); return; }
        if (!remarks.trim()) { toast.warn('Please enter remarks.'); return; }

        const formId = transferFrom === 'Bank'
            ? (fromBank?.BankId || 0)
            : (fromWallet?.WalletId || 0);

        dispatch(submitLoadWallet({
            Transferfrom:    transferFrom,
            FormId:          formId,
            ToId:            toWallet.WalletId,
            Modeofpay:       modeOfPay,
            TransactionNo:   transactionNo.trim(),
            TransferAmount:  parseFloat(amount),
            TransactionDate: date,
            Remarks:         remarks.trim(),
            RoleId:          roleId,
            Createdby:       userName,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────────
    const numAmount     = parseFloat(amount) || 0;
    const amountDisplay = getAmountDisplay(numAmount);

    // To-wallet list for Bank mode = all wallets; for Wallet mode = toWallets from API
    const toWalletList = transferFrom === 'Bank' ? wallets : toWallets;

    const sourceLabel = transferFrom === 'Bank'
        ? (fromBank?.BankName || '—')
        : (fromWallet?.WalletName || '—');

    const showSummary = transferFrom && (fromBank || fromWallet) && toWallet &&
        modeOfPay && transactionNo.trim() && date && numAmount > 0;

    const canSubmit   = showSummary && !saveLoading && remarks.trim();
    const isBusy      = saveLoading || bankLoading || walletsLoading;

    const dateDisplay = date
        ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
        : '—';


    const sourceMissing = (transferFrom === 'Bank' && !fromBank) || (transferFrom === 'Wallet' && !fromWallet);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Wallet className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Load Wallet'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · Transfer funds from Bank or Wallet to a GST Wallet</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={ArrowRight} title="Transfer Details" />
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">

                        {/* Transfer From Type */}
                        <div>
                            <Label required>Transfer From</Label>
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={transferFrom}
                                    onChange={(e) => setTransferFrom(e.target.value)}
                                    disabled={isBusy}
                                >
                                    <option value="">— Select Type —</option>
                                    {TRANSFER_FROM_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={false} />
                            </div>
                        </div>

                        {/* Source — bank or wallet */}
                        <div>
                            <Label required>{transferFrom === 'Wallet' ? 'Source Wallet' : 'Source Bank'}</Label>
                            <div className="relative">
                                {transferFrom === 'Wallet' ? (
                                    <select
                                        className={`${inputCls} appearance-none pr-8`}
                                        value={fromWallet?.WalletId || ''}
                                        onChange={(e) => {
                                            const w = wallets.find(x => x.WalletId === parseInt(e.target.value));
                                            setFromWallet(w || null);
                                            setToWallet(null);
                                        }}
                                        disabled={walletsLoading || isBusy}
                                    >
                                        <option value="">{walletsLoading ? 'Loading…' : '— Select Source Wallet —'}</option>
                                        {wallets.map((w) => (
                                            <option key={w.WalletId} value={w.WalletId}>{w.WalletName}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <select
                                        className={`${inputCls} appearance-none pr-8`}
                                        value={fromBank?.BankId || ''}
                                        onChange={(e) => {
                                            const bank = bankList.find(b => b.BankId === parseInt(e.target.value));
                                            setFromBank(bank || null);
                                            setToWallet(null);
                                        }}
                                        disabled={!transferFrom || bankLoading || isBusy}
                                    >
                                        <option value="">{!transferFrom ? 'Select Transfer From first' : bankLoading ? 'Loading…' : '— Select Bank —'}</option>
                                        {bankList.map((b) => (
                                            <option key={b.BankId} value={b.BankId}>{b.BankName}</option>
                                        ))}
                                    </select>
                                )}
                                <SelectIcon loading={transferFrom === 'Wallet' ? walletsLoading : bankLoading} />
                            </div>
                            {transferFrom === 'Bank' && fromBank && (
                                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                    A/c {fromBank.AccountNo || '—'} · {fromBank.AccountType || '—'} ·{' '}
                                    <span className="font-semibold text-indigo-700 dark:text-indigo-300">Bal ₹ {formatIndianCurrency(fromBank.AvailableBalance)}</span>
                                </p>
                            )}
                        </div>

                        {/* To Wallet */}
                        <div>
                            <Label required>Destination Wallet</Label>
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={toWallet?.WalletId || ''}
                                    onChange={(e) => {
                                        const w = toWalletList.find(x => x.WalletId === parseInt(e.target.value));
                                        setToWallet(w || null);
                                    }}
                                    disabled={!transferFrom || walletsLoading || toWalletsLoading || isBusy || sourceMissing}
                                >
                                    <option value="">
                                        {toWalletsLoading || walletsLoading
                                            ? 'Loading…'
                                            : !transferFrom || sourceMissing
                                                ? 'Select source first'
                                                : '— Select Destination Wallet —'}
                                    </option>
                                    {toWalletList.map((w) => (
                                        <option key={w.WalletId} value={w.WalletId}>{w.WalletName}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={toWalletsLoading || walletsLoading} />
                            </div>
                        </div>

                        {/* Mode of Payment */}
                        <div>
                            <Label required>Mode of Payment</Label>
                            <div className="relative">
                                <select
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={modeOfPay}
                                    onChange={(e) => setModeOfPay(e.target.value)}
                                    disabled={!transferFrom || isBusy}
                                >
                                    <option value="">— Select Mode —</option>
                                    {MODE_OPTIONS.map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={false} />
                            </div>
                        </div>

                        {/* Transaction No */}
                        <div>
                            <Label required>Transaction / Ref No.</Label>
                            <div className="relative">
                                <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    className={`${inputCls} pl-8`}
                                    placeholder="Enter reference number"
                                    value={transactionNo}
                                    onChange={(e) => setTransactionNo(e.target.value)}
                                    disabled={isBusy}
                                />
                            </div>
                        </div>

                        {/* Date */}
                        <div>
                            <Label required>Transaction Date</Label>
                            <CustomDatePicker
                                value={date}
                                onChange={setDate}
                                disabled={isBusy}
                                maxDate={new Date()}
                            />
                        </div>

                        {/* Amount */}
                        <div>
                            <Label required>Transfer Amount (₹)</Label>
                            <div className="relative">
                                <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    disabled={isBusy}
                                    className={`${inputCls} pl-8`}
                                />
                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="sm:col-span-1 lg:col-span-2">
                            <Label required>Remarks</Label>
                            <textarea
                                rows={1}
                                placeholder="Describe the purpose of this wallet transfer…"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                disabled={isBusy}
                                className={`${inputCls} resize-y`}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Transfer Summary" />
                        <div className="p-4 space-y-3">
                            {(fromBank || fromWallet) && toWallet && (
                                <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-100 dark:border-indigo-800">
                                    <p className="flex-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 truncate text-center" title={sourceLabel}>{sourceLabel}</p>
                                    <ArrowRight className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                                    <p className="flex-1 text-xs font-bold text-purple-600 dark:text-purple-400 truncate text-center" title={toWallet.WalletName}>{toWallet.WalletName}</p>
                                </div>
                            )}
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="Transfer From" value={transferFrom} />
                                <SumRow label="Source"        value={transferFrom ? (sourceLabel === '—' ? '' : sourceLabel) : ''} />
                                {transferFrom === 'Bank' && fromBank && (
                                    <SumRow label="Available" value={`₹ ${formatIndianCurrency(fromBank.AvailableBalance)}`} />
                                )}
                                <SumRow label="To Wallet"     value={toWallet?.WalletName} />
                                <SumRow label="Mode"          value={modeOfPay} />
                                <SumRow label="Reference No"  value={transactionNo} />
                                <SumRow label="Date"          value={date ? dateDisplay : ''} />
                                <SumRow label="Created By"    value={userName} />
                            </dl>

                            <div className="px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
                                <p className="text-indigo-100 text-[10px] font-semibold uppercase tracking-wider">Transfer Amount</p>
                                <p className="text-2xl font-black">₹ {formatIndianCurrency(numAmount)}</p>
                                {numAmount > 0 && <p className="text-[11px] text-indigo-100 italic leading-snug">{amountDisplay.words}</p>}
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={handleReset}
                                    disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {saveLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                                        : <><Send className="h-4 w-4" /> Submit Transfer</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoadWallet;
